import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { SparkleOutline } from '../../../components/doodles/Doodles.js';
import { IconPen, IconWaveSmall } from '../icons.js';
import { describeTurnAudioError, fetchTurnAudioBlob } from '../../../lib/sessionsApi.js';
import { useAudioBlob } from '../../../lib/audioBlobPlayer.js';
import { annotateText } from './annotate.js';
import { renderTapRange, tokenizeForTap } from './tappableText.js';
import { useBubbleWordLookup } from './useBubbleWordLookup.js';
import { EscucharBtn } from './bubbleActions.js';
import { ErrorDetail } from './ErrorDetail.js';
import { SkillChip } from './SkillChip.js';
import { CleanSentenceTick } from './CleanSentenceTick.js';
import { GameStampBadge } from './GameStamp.js';
import type { GameBubbleStamp } from '../gameArena.js';
import type { ConvError, ConvErrorSeverity } from '../errorModel.js';
import { GRAM_SEVERITY_STYLE } from '../severityStyle.js';
import type { SkillChipModel } from '../skillChips.js';
import { ASSISTED_COPY } from '../assisted/assistedCopy.js';
import '../assisted/assisted.css';
import type { ExplainLangResolved } from '../../../lib/englishApi.js';

/**
 * Chips de corrección icon-only: las primeras N burbujas con errores muestran
 * además el label de la categoría ("gramática" / "fonética") para enseñar qué
 * significa cada ícono; después quedan icon-only. Contador en localStorage
 * (mismo patrón que TapWordHint).
 */
const ERR_CHIP_LABEL_KEY = 'orborix-errchip-label-v1';
const ERR_CHIP_LABEL_MAX = 5;

function readErrChipCount(): number {
  try {
    return Number(localStorage.getItem(ERR_CHIP_LABEL_KEY)) || 0;
  } catch {
    return 0;
  }
}

function bumpErrChipCount(): void {
  try {
    localStorage.setItem(ERR_CHIP_LABEL_KEY, String(readErrChipCount() + 1));
  } catch {
    /* ignore */
  }
}


interface UserBubbleProps {
  text: string;
  errors: ReadonlyArray<ConvError>;
  /** Estados de la sesión viva. En el historial (replay) se omiten todos. */
  correctionPending?: boolean;
  streaming?: boolean;
  /**
   * Sólo streaming: el turno ya se envió (tap del mic) y espera el transcript
   * confirmado del server. Muestra la fila "Enviando…" — feedback inmediato
   * del tap mientras el STT commitea (300-1000ms).
   */
  sending?: boolean;
  animate?: boolean;
  /**
   * Sólo streaming: offset donde termina el prefijo ESTABLE del partial vivo
   * (palabras confirmadas por dos emisiones consecutivas del STT). Lo que
   * sigue se pinta atenuado (`.live-draft`) — la re-hipótesis del STT se lee
   * como borrador, no como texto firme que "se equivoca". Ver
   * `useLiveTranscriptDraft`.
   */
  draftFromIdx?: number;
  /**
   * Abre los drills de pronunciación. Sólo en la sesión viva; en el historial
   * se omite y `ErrorDetail` no muestra el CTA "Practicar drills".
   */
  onPracticeDrill?: (err: ConvError) => void;
  sessionId?: string;
  /**
   * Índice de turno (1-based) para reproducir el audio REAL del usuario
   * grabado en la sesión (RECORD_SESSION_AUDIO). Sólo en burbujas no-streaming
   * de la sesión viva. Sin esto, no se muestra el botón de escuchar.
   */
  replayTurnIndex?: number;
  /** Idioma persistido de la "Expandir explicación" (prefs de conversación). */
  explainLang?: ExplainLangResolved | null;
  /** Persiste la elección de idioma de la explicación (toggle del user). */
  onSetExplainLang?: (lang: ExplainLangResolved) => void;
  /**
   * The one positive skill chip of this bubble (W6), already decided by
   * `selectSkillChips` (verdict in, not contradicted, one per bubble). It goes
   * AFTER the correction chips, in the same row.
   */
  skillChip?: SkillChipModel | undefined;
  /**
   * The discreet "no important errors" ✓ (D10), already decided by
   * `selectCleanSentenceTicks` (ok verdict, 5+ words, no important error). Same
   * slot as the skill chip, which wins when both apply.
   */
  cleanTick?: boolean;
  /** Charla asistida: la notita de lo dicho con la respuesta modelo («Con ayuda · la dijiste»). */
  assistedNote?: string | null;
  /** Charla asistida: tapada mientras el alumno hace la escena solo (no le sopla la respuesta). */
  covered?: boolean;
  /**
   * The translate-it stamp of this attempt (plan 47), already decided by
   * `bubbleGameStamp` (judging, or a final stamp). Shown in the header row,
   * whatever the corrections view: in a game it is the scoreboard, not a
   * correction.
   */
  gameStamp?: GameBubbleStamp | undefined;
  /** The stamp slot is a live region (live thread only). */
  gameStampLive?: boolean;
  /**
   * The attempt's correction chips are concealed while its retry is pending
   * (plan 47): no underlines, the chip row blurred with «tocá para ver»;
   * tapping it calls this and reveals them.
   */
  onRevealConcealed?: (() => void) | undefined;
  /** Under the bubble: the attempt's retry / skip control, if it has one. */
  footer?: ReactNode;
}

export function UserBubble({
  text,
  errors,
  correctionPending = false,
  streaming = false,
  sending = false,
  animate = false,
  draftFromIdx,
  onPracticeDrill,
  sessionId,
  replayTurnIndex,
  explainLang,
  onSetExplainLang,
  skillChip,
  cleanTick = false,
  assistedNote = null,
  covered = false,
  gameStamp,
  gameStampLive = false,
  onRevealConcealed,
  footer,
}: UserBubbleProps) {
  const [openId, setOpenIdRaw] = useState<string | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  // Tocar una palabra propia: lo mismo que en la burbuja del coach. Las
  // palabras de un subrayado siguen abriendo su corrección.
  const { wordSel, overlay: wordOverlay } = useBubbleWordLookup(cardRef, text, {
    origin: 'learner',
    sessionId,
  });
  const tapTokens = useMemo(() => tokenizeForTap(text), [text]);
  const selRange: readonly [number, number] | null = wordSel.sel
    ? [wordSel.sel.start, wordSel.sel.end]
    : null;
  const renderTap = (lo: number, hi: number, inError: boolean) =>
    renderTapRange(tapTokens, lo, hi, {
      // Tapada (lo dicho con ayuda, mientras lo hace solo): tocar una palabra
      // borrosa la mostraría en el popover.
      tappable: !inError && !covered,
      selRange,
      onWordTap: (word, el, idx) => {
        setOpenIdRaw(null);
        wordSel.handleWordTap(word, el, idx);
      },
    });
  // Una cosa abierta a la vez: la corrección o la palabra.
  const closeWord = wordSel.closeWord;
  const setOpenId = (id: string | null): void => {
    if (id) closeWord();
    setOpenIdRaw(id);
  };
  const replay = useAudioBlob();
  /** Motivo del último fallo del replay (para el tooltip del botón). */
  const [replayErr, setReplayErr] = useState<string | null>(null);
  const canReplay = !streaming && sessionId != null && replayTurnIndex != null;
  // A retried attempt hides its corrections until tapped (plan 47): the
  // answer must not be read off the screen while the learner tries again.
  const concealed = !streaming && onRevealConcealed !== undefined && errors.length > 0;
  const visibleErrors = streaming || concealed ? [] : errors;
  const hasErrors = visibleErrors.length > 0;
  const positiveChip = streaming ? undefined : skillChip;
  // Never stacked with the skill chip: the chip is the stronger praise.
  const showCleanTick = !streaming && cleanTick && !positiveChip;
  // Label de los chips: decidido UNA vez al montar (una burbuja no flipa a
  // icon-only en vivo cuando el contador cruza el umbral).
  const [showChipLabel] = useState(() => readErrChipCount() < ERR_CHIP_LABEL_MAX);
  const bumpedRef = useRef(false);
  useEffect(() => {
    if (hasErrors && !bumpedRef.current) {
      bumpedRef.current = true;
      bumpErrChipCount();
    }
  }, [hasErrors]);
  const openErr = hasErrors ? (visibleErrors.find((e) => e.id === openId) ?? null) : null;
  const gramErrors = visibleErrors.filter((e) => e.kind === 'grammar');
  const gramCount = gramErrors.length;
  const phonCount = visibleErrors.filter((e) => e.kind === 'phonetic').length;
  // Severidad más grave de los grammar errors → color del chip de abajo.
  // Mismo orden que SEVERITY_RANK: communication-breaking > structural > minor > suggestion.
  const SEV_ORDER: ConvErrorSeverity[] = ['communication-breaking', 'structural', 'minor', 'suggestion'];
  const worstGramSev: ConvErrorSeverity =
    SEV_ORDER.find((s) => gramErrors.some((e) => e.severity === s)) ?? 'structural';
  // A translate-it attempt (plan 47 Round 2 review): the chip sits next to the
  // stamp, so it names the SEVERITY, not the category, and always shows it.
  // «gramática» on a yellow chip next to a «¡Bien!» (only a minor slip) read
  // as a contradiction; now it reads «detalle» / «más natural», and next to a
  // «Casi» / «No era eso» the chip reads «gramática» / «error grave». Scoped
  // to the game: elsewhere the label keeps teaching what the pen icon means.
  // A real error next to a «¡Perfecto!» / «¡Bien!» can only sit OUTSIDE the
  // judged sentence (the stamp reads the chips inside it): the chip says so,
  // «fuera del desafío», instead of «gramática» contradicting the stamp.
  const errorOutsideChallenge =
    (gameStamp === 'perfect' || gameStamp === 'good') &&
    (worstGramSev === 'structural' || worstGramSev === 'communication-breaking');
  const gameGramLabel =
    gameStamp === undefined
      ? null
      : errorOutsideChallenge
        ? 'fuera del desafío'
        : GRAM_SEVERITY_STYLE[worstGramSev].tag.toLocaleLowerCase('es');
  const gramLabel = gameGramLabel ?? 'gramática';
  const showGramLabel = gameGramLabel !== null || showChipLabel;
  const gramAria =
    gameGramLabel !== null
      ? `${gramCount} ${gramCount === 1 ? 'corrección' : 'correcciones'}: ${gameGramLabel}`
      : `${gramCount} ${gramCount === 1 ? 'error' : 'errores'} de gramática`;

  /** Rango de severidad (0 = más grave). Para fonética no aplica → al final. */
  const sevRank = (e: ConvError): number => {
    const i = SEV_ORDER.indexOf(e.severity);
    return i === -1 ? SEV_ORDER.length : i;
  };
  /**
   * Errores de un tipo ordenados del MÁS severo al menos. El sort de JS es
   * estable, así que dentro de la misma severidad se conserva el orden de
   * aparición. Es el orden con que el chip abre y la card navega.
   */
  const orderedOf = (kind: ConvError['kind']): ConvError[] =>
    visibleErrors.filter((e) => e.kind === kind).sort((a, b) => sevRank(a) - sevRank(b));

  /**
   * Cicla por los errores de un tipo (ordenados por severidad): si ninguno
   * abierto → abre el MÁS severo; en cada click avanza al siguiente; tras el
   * último → cierra. Permite descubrir los N errores (antes el chip abría
   * siempre el primero y no se podía llegar al resto cuando los subrayados
   * inline quedaban solapados).
   */
  const cycleKind = (kind: ConvError['kind']) => {
    const list = orderedOf(kind);
    if (list.length === 0) return;
    const idx = list.findIndex((e) => e.id === openId);
    if (idx === -1) setOpenId(list[0]!.id);
    else if (idx >= list.length - 1) setOpenId(null);
    else setOpenId(list[idx + 1]!.id);
  };

  return (
    <div
      style={{
        alignSelf: 'flex-end',
        maxWidth: '82%',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        alignItems: 'flex-end',
      }}
      /* Perf: cerradas fuera del viewport no pagan layout/paint (conv-cv).
         ErrorDetail es in-flow (crece el root), no lo recorta; el popover de
         palabra sí desborda, así que con él abierto se apaga. */
      className={[!streaming && !wordSel.sel ? 'conv-cv' : '', animate ? 'slide-up' : ''].filter(Boolean).join(' ') || undefined}
    >
      <div
        className={`card user${covered ? ' asst-covered' : ''}`}
        style={{ width: '100%', minWidth: 96 }}
        ref={cardRef}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 14,
          }}
        >
          <span className="script" style={{ color: 'var(--purple)', fontSize: 22, lineHeight: 1 }}>
            Tú
          </span>
          {/* El slot derecho es INTERACTIVO (play) o vacío: el sparkle
              decorativo sólo vive en la burbuja viva — en una cerrada parecía
              un botón que no hacía nada. */}
          {gameStamp !== undefined && !streaming ? (
            <span className="game-stamp-head">
              <GameStampBadge stamp={gameStamp} live={gameStampLive} />
            </span>
          ) : null}
          {canReplay ? (
            <EscucharBtn
              tone="purple"
              playing={replay.state === 'playing'}
              loading={replay.state === 'loading'}
              errored={replay.state === 'error'}
              {...(replayErr ? { errorHint: replayErr } : {})}
              onToggle={() => {
                setReplayErr(null);
                void replay.toggle(async () => {
                  try {
                    return await fetchTurnAudioBlob(sessionId, replayTurnIndex, 'user', {
                      retries: 3,
                    });
                  } catch (err) {
                    // `useAudioBlob` se traga el error (sólo deja `state:
                    // 'error'`), así que lo capturamos ACÁ: sin el motivo, un
                    // 404 del audio y un fallo de reproducción son
                    // indistinguibles y no se puede diagnosticar nada.
                    setReplayErr(describeTurnAudioError(err));
                    throw err;
                  }
                });
              }}
            />
          ) : streaming ? (
            <span aria-hidden="true" style={{ display: 'inline-flex' }}>
              <SparkleOutline color="var(--purple)" size={22} />
            </span>
          ) : null}
        </div>
        <div className="body">
          {!streaming ? (
            annotateText(text, visibleErrors, openId, setOpenId, renderTap)
          ) : draftFromIdx !== undefined && draftFromIdx < text.length ? (
            <>
              {text.slice(0, draftFromIdx)}
              <span className="live-draft">{text.slice(draftFromIdx)}</span>
            </>
          ) : (
            text
          )}
          {streaming && (
            <>
              {text && !/\s$/.test(text) ? ' ' : ''}
              <span className="stream-caret" aria-hidden="true" />
            </>
          )}
        </div>
        {(assistedNote || covered) && (
          <div className="asst-bubble-note">
            {covered ? ASSISTED_COPY.es.bubble.covered : assistedNote}
          </div>
        )}

        {/* Feedback del tap de enviar: el STT está commiteando (300-1000ms) y
            sin esta fila la app parecía frizada. Mismo layout que la fila
            "Analizando" que la reemplaza al confirmarse el transcript. */}
        {streaming && sending && (
          <div
            role="status"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              margin: '6px 0 10px',
              minHeight: 26,
              color: 'var(--text-muted)',
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: 11.5,
            }}
          >
            <span className="conv-typing-dots" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
            <span>Enviando</span>
          </div>
        )}

        {/* Mismos márgenes + minHeight que la fila de chips que lo reemplaza:
            la transición analizando→chips no salta el layout. Color muted (no
            faint): faint quedaba bajo 3:1 de contraste a 11.5px. */}
        {!streaming && correctionPending && (
          <div
            role="status"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              margin: '6px 0 10px',
              minHeight: 26,
              color: 'var(--text-muted)',
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: 11.5,
            }}
          >
            <span className="conv-typing-dots" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
            <span>Analizando</span>
          </div>
        )}

        {/* Chips icon-only: ícono + badge de conteo (Bloque D.1). El chip
            positivo de habilidad (W6) va al final de la misma fila. */}
        {/* Concealed corrections of a retried attempt: one blurred row. */}
        {concealed && (
          <div style={{ display: 'flex', margin: '6px 0 10px' }}>
            <button
              type="button"
              className="game-conceal"
              onClick={onRevealConcealed}
              aria-label="Mostrar las correcciones de este intento"
            >
              <span className="game-conceal__blur" aria-hidden="true">
                <IconPen size={12} />
                gramática
              </span>
              <span className="game-conceal__label">tocá para ver</span>
            </button>
          </div>
        )}

        {/* The D10 ✓ takes the positive chip's slot when there is no chip. */}
        {(hasErrors || positiveChip || showCleanTick) && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-start', margin: '6px 0 10px' }}>
            {gramCount > 0 && (
              <button
                type="button"
                className={`err-chip-ic err-chip-grammar err-chip-sev-${worstGramSev} ${showGramLabel ? 'with-label' : ''} ${visibleErrors.some((e) => e.kind === 'grammar' && e.id === openId) ? 'is-active' : ''}`}
                onClick={() => cycleKind('grammar')}
                aria-label={gramAria}
                title={gramAria}
              >
                <IconPen size={12} />
                {showGramLabel && <span className="err-chip-ic-label">{gramLabel}</span>}
                {gramCount > 1 && <span className="err-chip-ic-count">{gramCount}</span>}
              </button>
            )}
            {phonCount > 0 && (
              <button
                type="button"
                className={`err-chip-ic err-chip-phonetic ${showChipLabel ? 'with-label' : ''} ${visibleErrors.some((e) => e.kind === 'phonetic' && e.id === openId) ? 'is-active' : ''}`}
                onClick={() => cycleKind('phonetic')}
                aria-label={`${phonCount} ${phonCount === 1 ? 'error' : 'errores'} de fonética`}
                title={`${phonCount} ${phonCount === 1 ? 'error' : 'errores'} de fonética`}
              >
                <IconWaveSmall size={12} />
                {showChipLabel && <span className="err-chip-ic-label">fonética</span>}
                {phonCount > 1 && <span className="err-chip-ic-count">{phonCount}</span>}
              </button>
            )}
            {positiveChip && <SkillChip chip={positiveChip} />}
            {showCleanTick && <CleanSentenceTick />}
          </div>
        )}

        {wordOverlay}
      </div>

      {footer}

      {openErr && (
        <ErrorDetail
          key={openErr.id}
          err={openErr}
          onClose={() => setOpenId(null)}
          explainLang={explainLang ?? null}
          {...(onPracticeDrill ? { onPractice: onPracticeDrill } : {})}
          {...(onSetExplainLang ? { onSetExplainLang } : {})}
          {...(sessionId ? { sessionId } : {})}
          {...(() => {
            // Navegación prev/next por los errores del MISMO tipo que el abierto,
            // ordenados del más severo al menos (coherente con el chip que lo
            // abrió). Wrap-around.
            const list = orderedOf(openErr.kind);
            const idx = list.findIndex((e) => e.id === openErr.id);
            if (list.length <= 1 || idx < 0) return {};
            const n = list.length;
            return {
              nav: {
                index: idx + 1,
                total: n,
                onPrev: () => setOpenId(list[(idx - 1 + n) % n]!.id),
                onNext: () => setOpenId(list[(idx + 1) % n]!.id),
              },
            };
          })()}
        />
      )}
    </div>
  );
}
